import type { ComponentInit } from "@scripts/registry";

const TRAIL = "[data-fa-breadcrumb-trail]";
const OVERFLOW = "[data-fa-breadcrumb-overflow]";
const TRIGGER = "[data-fa-breadcrumb-trigger]";
const PANEL = "[data-fa-breadcrumb-panel]";
const PANEL_LIST = "[data-fa-breadcrumb-panel-list]";
const ITEM = "[data-fa-breadcrumb-item]";
const CURRENT = "[data-fa-breadcrumb-current]";

const MEASURED = "fa-breadcrumb--measured";
const TRUNCATED = "fa-breadcrumb__item--truncated";
const CONDENSED = "fa-breadcrumb--condensed";
const INDEX = "--fa-breadcrumb-index";
const PANEL_MAX = "--fa-breadcrumb-panel-max";

export const initBreadcrumb: ComponentInit = (root) => {
	const trail = root.querySelector<HTMLElement>(TRAIL);
	const overflow = root.querySelector<HTMLElement>(OVERFLOW);
	const trigger = root.querySelector<HTMLElement>(TRIGGER);
	const panel = root.querySelector<HTMLElement>(PANEL);
	const panelList = root.querySelector<HTMLElement>(PANEL_LIST);
	const current = root.querySelector<HTMLElement>(CURRENT);

	if (!trail || !overflow || !trigger || !panel || !panelList || !current) return;

	const items = Array.from(trail.querySelectorAll<HTMLElement>(ITEM));

	if (items.length === 0) return;

	const authored = Array.from(trail.children);
	const listeners = new AbortController();
	const { signal } = listeners;

	const setOpen = (open: boolean) => {
		trigger.setAttribute("aria-expanded", String(open));
		panel.hidden = !open;
	};

	const restoreOrder = () => {
		for (const node of authored) trail.append(node);
	};

	const clearTruncation = () => {
		for (const item of items) {
			item.classList.remove(TRUNCATED);
			item.style.removeProperty(INDEX);
		}
		current.classList.remove(TRUNCATED);
	};

	const fit = (budget: number, widths: Map<HTMLElement, number>, gap: number) => {
		const kept: HTMLElement[] = [];
		let remaining = budget;

		for (let index = items.length - 1; index >= 0; index -= 1) {
			const item = items[index];

			if (!item) break;

			const needed = (widths.get(item) ?? 0) + gap;

			if (needed > remaining) break;

			kept.unshift(item);
			remaining -= needed;
		}

		return kept;
	};

	const layout = () => {
		/* Captured before restoreOrder: re-appending a focused <li> detaches it, so reading
		   activeElement afterwards returns <body> and the restore below refocuses nothing. */
		const focused = document.activeElement;

		restoreOrder();
		clearTruncation();
		overflow.hidden = false;

		const gap = Number.parseFloat(getComputedStyle(trail).columnGap) || 0;
		const widths = new Map(
			items.map((item) => [item, item.getBoundingClientRect().width] as const),
		);
		const triggerWidth = overflow.getBoundingClientRect().width;
		const available = trail.getBoundingClientRect().width;
		const budget = available - current.getBoundingClientRect().width - gap;

		let kept = fit(budget, widths, gap);

		if (kept.length < items.length) kept = fit(budget - triggerWidth - gap, widths, gap);

		const last = items[items.length - 1];
		const floored = kept.length === 0 && last ? [last] : kept;
		const hidden = items.filter((item) => !floored.includes(item));

		for (const [index, item] of hidden.entries()) {
			item.style.setProperty(INDEX, String(index));
			panelList.append(item);
		}
		for (const item of floored) trail.insertBefore(item, current);

		if (kept.length === 0) floored[0]?.classList.add(TRUNCATED);

		overflow.hidden = hidden.length === 0;
		root.classList.toggle(CONDENSED, hidden.length > 0);

		if (hidden.length === 0) setOpen(false);

		const room = trail.getBoundingClientRect().right - overflow.getBoundingClientRect().left;

		panel.style.setProperty(PANEL_MAX, `${String(Math.round(room))}px`);

		if (trail.scrollWidth > Math.ceil(trail.getBoundingClientRect().width)) {
			current.classList.add(TRUNCATED);
		}

		if (focused instanceof HTMLElement && focused.isConnected) {
			focused.focus({ preventScroll: true });
		}
	};

	root.classList.add(MEASURED);
	layout();

	let width = Math.round(trail.getBoundingClientRect().width);
	let frame = 0;

	const observer = new ResizeObserver((entries) => {
		const next = Math.round(entries[0]?.borderBoxSize[0]?.inlineSize ?? width);

		if (next === width) return;

		width = next;

		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(layout);
	});

	observer.observe(trail);

	let disposed = false;

	/* A late webfont re-wraps every label without resizing the bar, so the observer never fires
	   and the split stays measured against the fallback font's metrics. */
	void document.fonts?.ready.then(() => {
		if (!disposed) layout();
	});

	trigger.addEventListener(
		"click",
		() => {
			setOpen(trigger.getAttribute("aria-expanded") !== "true");
		},
		{ signal },
	);

	document.addEventListener(
		"click",
		(event) => {
			if (panel.hidden) return;
			if (event.target instanceof Node && overflow.contains(event.target)) return;

			setOpen(false);
		},
		{ signal },
	);

	document.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Escape" || panel.hidden) return;

			setOpen(false);
			trigger.focus({ preventScroll: true });
		},
		{ signal },
	);

	return () => {
		disposed = true;
		cancelAnimationFrame(frame);
		observer.disconnect();
		listeners.abort();
		restoreOrder();
		clearTruncation();
		panel.style.removeProperty(PANEL_MAX);
		root.classList.remove(MEASURED);
		root.classList.remove(CONDENSED);
		overflow.hidden = true;
		setOpen(false);
	};
};
